import * as T from './redux.actionTypes';
import { DEFAULT_PAGE_SIZE } from './common';

const initialState = {
    teams: [],
    loading: false,
    error: null,
    detail: null,
    detailLoading: false,
    detailError: null,
    pagination: { limit: DEFAULT_PAGE_SIZE, offset: 1, total: 0 },
    filters: { search: '', isActive: '' },
    sort: { sortBy: 'updatedAt', sortDescending: true },
    isTeamDialogOpen: false,
    isDeleteDialogOpen: false,
    isDetailDialogOpen: false,
    selectedTeam: null,

    // Employee options for member picker
    employeeOptions: [],
    employeeOptionsLoading: false,

    // Set after a team is created so the UI can navigate to its detail page
    lastCreatedTeamId: null
};

const replaceById = (arr, item) => {
    if (!Array.isArray(arr) || !item) return arr;
    let changed = false;
    const next = arr.map((x) => {
        if (x && x.id === item.id) { changed = true; return item; }
        return x;
    });
    return changed ? next : [...arr, item];
};

const updateDetailField = (state, key, updater) => {
    if (!state.detail) return state;
    const current = state.detail[key];
    const next = updater(Array.isArray(current) ? current : []);
    if (next === current) return state;
    return { ...state, detail: { ...state.detail, [key]: next } };
};

const teamReducer = (state = initialState, action) => {
    switch (action.type) {
        case T.FETCH_TEAMS:
            return { ...state, loading: true, error: null };
        case T.FETCH_TEAMS_SUCCESS:
            return {
                ...state,
                loading: false,
                teams: action.payload.data || [],
                pagination: { ...state.pagination, total: action.payload.total || 0 }
            };
        case T.FETCH_TEAMS_FAILURE:
            return { ...state, loading: false, error: action.payload };

        case T.FETCH_TEAM_DETAIL:
            return { ...state, detailLoading: true, detailError: null };
        case T.FETCH_TEAM_DETAIL_SUCCESS:
            return { ...state, detailLoading: false, detail: action.payload };
        case T.FETCH_TEAM_DETAIL_FAILURE:
            return { ...state, detailLoading: false, detailError: action.payload };
        case T.CLEAR_TEAM_DETAIL:
            return { ...state, detail: null, detailError: null };

        case T.CREATE_TEAM:
        case T.UPDATE_TEAM:
        case T.DELETE_TEAM:
            return { ...state, loading: true, error: null };
        case T.CREATE_TEAM_SUCCESS:
            return {
                ...state,
                loading: false,
                lastCreatedTeamId: action.payload && action.payload.id ? action.payload.id : state.lastCreatedTeamId
            };
        case T.UPDATE_TEAM_SUCCESS: {
            const updated = action.payload;
            const nextTeams = updated && updated.id ? replaceById(state.teams, updated) : state.teams;
            const nextDetail = state.detail && updated && state.detail.id === updated.id ? { ...state.detail, ...updated } : state.detail;
            return { ...state, loading: false, teams: nextTeams, detail: nextDetail };
        }
        case T.DELETE_TEAM_SUCCESS:
            return { ...state, loading: false };
        case T.CREATE_TEAM_FAILURE:
        case T.UPDATE_TEAM_FAILURE:
        case T.DELETE_TEAM_FAILURE:
            return { ...state, loading: false, error: action.payload };

        case T.CLEAR_LAST_CREATED_TEAM:
            return { ...state, lastCreatedTeamId: null };

        // ===== Members: granular updates so only the members section re-renders =====
        case T.ADD_MEMBER_SUCCESS:
            return updateDetailField(state, 'members', (arr) => {
                const item = action.payload;
                if (!item) return arr;
                const idx = arr.findIndex((x) => (x.employeeEmail || '').toLowerCase() === (item.employeeEmail || '').toLowerCase());
                if (idx === -1) return [...arr, item];
                const next = arr.slice(); next[idx] = item; return next;
            });
        case T.UPDATE_MEMBER_SUCCESS:
            return updateDetailField(state, 'members', (arr) => {
                const item = action.payload;
                if (!item) return arr;
                const idx = arr.findIndex((x) => (x.employeeEmail || '').toLowerCase() === (item.employeeEmail || '').toLowerCase());
                if (idx === -1) return arr;
                const next = arr.slice(); next[idx] = item; return next;
            });
        case T.REMOVE_MEMBER_SUCCESS:
            return updateDetailField(state, 'members', (arr) =>
                arr.filter((x) => (x.employeeEmail || '').toLowerCase() !== (action.payload.email || '').toLowerCase())
            );

        case T.OPEN_TEAM_DIALOG:
            return { ...state, isTeamDialogOpen: true, selectedTeam: action.payload };
        case T.CLOSE_TEAM_DIALOG:
            return { ...state, isTeamDialogOpen: false, selectedTeam: null };
        case T.OPEN_DELETE_DIALOG:
            return { ...state, isDeleteDialogOpen: true, selectedTeam: action.payload };
        case T.CLOSE_DELETE_DIALOG:
            return { ...state, isDeleteDialogOpen: false, selectedTeam: null };
        case T.OPEN_DETAIL_DIALOG:
            return { ...state, isDetailDialogOpen: true, selectedTeam: action.payload };
        case T.CLOSE_DETAIL_DIALOG:
            return { ...state, isDetailDialogOpen: false, selectedTeam: null, detail: null };

        case T.SET_FILTERS:
            return { ...state, filters: { ...state.filters, ...action.payload } };
        case T.SET_PAGINATION:
            return { ...state, pagination: { ...state.pagination, ...action.payload } };
        case T.SET_SORT:
            return { ...state, sort: { ...state.sort, ...action.payload } };

        case T.FETCH_EMPLOYEE_OPTIONS:
            return { ...state, employeeOptionsLoading: true };
        case T.FETCH_EMPLOYEE_OPTIONS_SUCCESS:
            return { ...state, employeeOptionsLoading: false, employeeOptions: Array.isArray(action.payload) ? action.payload : [] };
        case T.FETCH_EMPLOYEE_OPTIONS_FAILURE:
            return { ...state, employeeOptionsLoading: false };

        default:
            return state;
    }
};

export default teamReducer;
